import { create } from 'zustand';
import type { ParsedObjectUpdate } from '../components/chat/objectUpdateParser';
import { stageObjectUpdate } from '../components/chat/objectDraftRegistry';
import { CREDENTIAL_OBJECT_KIND } from '../utils/chatEditableObjectUtils';
import { navigateToApplicationSettingsSubTab } from '../utils/navigateToApplicationSettingsSubTab';
import { useCustomRoleUpdateQueueStore } from './customRoleUpdateQueueStore';
import { useCredentialEditStore } from './credentialEditStore';
import { useCredentialAuthMaterialEditStore } from './credentialAuthMaterialEditStore';

function mergeTrackedCredentialIds({
  existingIds,
  updates,
}: {
  existingIds: string[];
  updates: ParsedObjectUpdate[];
}): string[] {
  const ids = new Set(existingIds);
  for (const update of updates) {
    if (update.objectKind === CREDENTIAL_OBJECT_KIND) {
      ids.add(update.objectId);
    }
  }
  return [...ids];
}

type CredentialUpdateQueueStore = {
  queue: ParsedObjectUpdate[];
  sourceChatId: string | null;
  trackedCredentialIds: string[];
  enqueueCredentialUpdates: ({
    updates,
    chatId,
  }: {
    updates: ParsedObjectUpdate[];
    chatId: string;
  }) => void;
  tryActivateCredentialUpdates: () => void;
  removeCredentialUpdatesByIds: (ids: string[]) => void;
  clearCredentialUpdateQueue: () => void;
  releaseCredentialUpdateStateForChat: (chatId: string) => void;
};

export const useCredentialUpdateQueueStore = create<CredentialUpdateQueueStore>(
  (set, get) => ({
    queue: [],
    sourceChatId: null,
    trackedCredentialIds: [],

    enqueueCredentialUpdates: ({ updates, chatId }) => {
      const credentialUpdates = updates.filter(
        (update) => update.objectKind === CREDENTIAL_OBJECT_KIND,
      );
      if (credentialUpdates.length === 0) {
        return;
      }

      const state = get();
      const knownIds = new Set(state.queue.map((entry) => entry.id));
      const nextQueue = [...state.queue];
      for (const update of credentialUpdates) {
        if (knownIds.has(update.id)) {
          continue;
        }
        knownIds.add(update.id);
        nextQueue.push(update);
      }

      set({
        queue: nextQueue,
        sourceChatId: state.sourceChatId ?? chatId,
        trackedCredentialIds: mergeTrackedCredentialIds({
          existingIds: state.trackedCredentialIds,
          updates: credentialUpdates,
        }),
      });

      if (
        !useCustomRoleUpdateQueueStore.getState().hasPendingCustomRoleWork()
      ) {
        get().tryActivateCredentialUpdates();
      }
    },

    tryActivateCredentialUpdates: () => {
      if (useCustomRoleUpdateQueueStore.getState().hasPendingCustomRoleWork()) {
        return;
      }

      const state = get();
      if (state.queue.length === 0) {
        return;
      }

      let stagedAny = false;
      const remainingQueue: ParsedObjectUpdate[] = [];
      for (const update of state.queue) {
        const staged = stageObjectUpdate({
          objectKind: update.objectKind,
          objectId: update.objectId,
          patch: update.patch,
          reason: update.reason,
          sourceUpdateId: update.id,
        });
        if (staged) {
          stagedAny = true;
          continue;
        }
        remainingQueue.push(update);
      }

      if (!stagedAny) {
        return;
      }

      navigateToApplicationSettingsSubTab('credentials');

      set({
        queue: remainingQueue,
      });
    },

    removeCredentialUpdatesByIds: (ids) => {
      const idsToRemove = new Set(ids.filter((id) => id.length > 0));
      if (idsToRemove.size === 0) {
        return;
      }

      const state = get();
      const nextQueue = state.queue.filter(
        (update) => !idsToRemove.has(update.id),
      );
      if (nextQueue.length === state.queue.length) {
        return;
      }

      set({
        queue: nextQueue,
      });
    },

    clearCredentialUpdateQueue: () => {
      set({
        queue: [],
        sourceChatId: null,
        trackedCredentialIds: [],
      });
    },

    releaseCredentialUpdateStateForChat: (chatId) => {
      const state = get();
      if (state.sourceChatId !== chatId) {
        return;
      }

      const credentialEditStore = useCredentialEditStore.getState();
      const credentialAuthMaterialEditStore =
        useCredentialAuthMaterialEditStore.getState();
      const sourceUpdateIds = state.queue.map((update) => update.id);

      credentialEditStore.clearPendingEditsForSourceUpdateIds(sourceUpdateIds);
      credentialAuthMaterialEditStore.clearPendingEditsForSourceUpdateIds(
        sourceUpdateIds,
      );
      for (const credentialId of state.trackedCredentialIds) {
        credentialEditStore.clearCredentialEditState(credentialId);
        credentialAuthMaterialEditStore.clearCredentialAuthMaterialEditState(
          credentialId,
        );
      }

      set({
        queue: [],
        sourceChatId: null,
        trackedCredentialIds: [],
      });
    },
  }),
);
